import { type FormEvent, type ReactNode, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as authApi from '../../api/auth';
import { useAuth } from '../../lib/auth';
import { ApiError } from '../../lib/http';
import { CamposSenha } from '../componentes/CamposSenha';
import { TelaAuthPortal } from '../componentes/TelaAuthPortal';
import { BlocoAviso, Botao, Campo } from '../componentes/ui';
import { validarNovaSenha } from '../lib/validacaoSenha';

/**
 * Recuperação de senha (UX §5.7) — duas telas na mesma rota `/recuperar`:
 * sem `?token=` → pede telefone, **resposta neutra sempre** (não confirma se
 * o número está cadastrado). Com `?token=` → o link que o cliente recebeu:
 * define a senha nova (`POST /v1/auth/redefinir-senha`) e loga automaticamente,
 * igual ao primeiro acesso.
 */
export function PortalRecuperar(): ReactNode {
  const [params] = useSearchParams();
  const token = params.get('token');
  return token ? <FormularioNovaSenha token={token} /> : <PedirLink />;
}

function PedirLink(): ReactNode {
  const [telefone, setTelefone] = useState('');
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const aoEnviar = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setEnviando(true);
    try {
      await authApi.recuperarSenha(telefone.trim());
    } catch {
      /* resposta neutra mesmo em falha — não vaza existência do cadastro */
    }
    setEnviado(true);
    setEnviando(false);
  };

  return (
    <TelaAuthPortal>
      {enviado ? (
        <div className="flex flex-col gap-5">
          <p className="tipo-corpo text-tinta">
            Se este número estiver cadastrado, você vai receber um link pelo WhatsApp.
          </p>
          <Link to="/" className="tipo-nota text-acento-texto underline">
            Voltar
          </Link>
        </div>
      ) : (
        <form onSubmit={(e) => void aoEnviar(e)} className="flex flex-col gap-5" noValidate>
          <p className="tipo-corpo text-tinta">
            Informe o telefone do seu cadastro e enviamos um link para você criar uma senha nova.
          </p>
          <Campo
            rotulo="Telefone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            autoFocus
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
          />
          <Botao type="submit" variante="primario" carregando={enviando}>
            {enviando ? 'Enviando…' : 'Enviar link'}
          </Botao>
          <Link to="/" className="tipo-nota text-acento-texto underline">
            Voltar
          </Link>
        </form>
      )}
    </TelaAuthPortal>
  );
}

function FormularioNovaSenha({ token }: { token: string }): ReactNode {
  const navigate = useNavigate();
  const { registrarSessao } = useAuth();

  const [senha, setSenha] = useState('');
  const [confirma, setConfirma] = useState('');
  const [erroSenha, setErroSenha] = useState<string | null>(null);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [linkInvalido, setLinkInvalido] = useState(false);
  const [enviando, setEnviando] = useState(false);

  if (linkInvalido) {
    return (
      <TelaAuthPortal>
        <div className="flex flex-col gap-5">
          <BlocoAviso>Este link expirou ou já foi usado.</BlocoAviso>
          <Link to="/recuperar" className="tipo-nota text-acento-texto underline">
            Pedir um novo link
          </Link>
        </div>
      </TelaAuthPortal>
    );
  }

  const aoEnviar = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setErroSenha(null);
    setErroGeral(null);

    const erro = validarNovaSenha(senha, confirma);
    if (erro) {
      setErroSenha(erro);
      return;
    }

    setEnviando(true);
    try {
      const dados = await authApi.redefinirSenha(token, senha);
      registrarSessao({
        accessToken: dados.accessToken,
        refreshToken: dados.refreshToken,
        usuario: authApi.usuarioDeResposta(dados),
      });
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && !err.ehRede && err.codigo === 'nao_autenticado') {
        setLinkInvalido(true);
      } else {
        setErroGeral('Não foi possível redefinir sua senha agora. Tente de novo.');
      }
      setEnviando(false);
    }
  };

  return (
    <TelaAuthPortal>
      <form onSubmit={(e) => void aoEnviar(e)} className="flex flex-col gap-5" noValidate>
        <div className="flex flex-col gap-2">
          <h1 className="tipo-display text-tinta-forte">Nova senha</h1>
          <p className="tipo-corpo text-tinta">Crie uma senha nova para entrar no seu portal.</p>
        </div>
        {erroGeral && <BlocoAviso>{erroGeral}</BlocoAviso>}
        <CamposSenha
          senha={senha}
          setSenha={setSenha}
          confirma={confirma}
          setConfirma={setConfirma}
          erro={erroSenha ?? undefined}
        />
        <Botao type="submit" variante="primario" carregando={enviando}>
          {enviando ? 'Salvando…' : 'Salvar nova senha'}
        </Botao>
      </form>
    </TelaAuthPortal>
  );
}
